<template>
  <div :name="name">
    <el-select
      class="w-100"
      size="small"
      v-bind="$attrs"
      :value="selectedValuesInternal"
      @focus="fetchOptions"
      multiple
      filterable
      clearable
      reserve-keyword
      :collapse-tags="shouldCollapseTags"
      @change="handleSelectChange"
      :loading="isLoading"
      :filter-method="debouncedFilterGroup"
      :ref="`select-${selectId}`"
      :popper-class="`popper-${selectId}`"
      @visible-change="onChangeDropDownPopper"
    >
      <template v-if="options.length > 0">
        <template v-if="!group">
          <el-option
            v-for="option in options"
            :key="option.value"
            :label="option.label"
            :value="option.id"
          ></el-option>
        </template>
        <template v-else>
          <el-option-group
            v-for="group in options"
            :key="group.label"
            :label="group.label">
            <el-option
              v-for="item in group.children"
              :key="item.value"
              :label="item.label"
              :value="item.id">
            </el-option>
          </el-option-group>
        </template>
      </template>
    </el-select>
  </div>
</template>

<script>
export default {
  props: {
    url: String,
    value: Array,
    group: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectId: null,
      name: '',
      options: [],
      tempOptions: [],
      loaded: false,
      currentUrl: this.url,
      selectedValuesInternal: [],
      tagContainerWidth: 0,
      maxVisibleTags: 3,
      debouncedFilterGroup: null,
      isTyping: false
    };
  },
  computed: {
    shouldCollapseTags() {
      return (
        this.selectedValuesInternal.length > this.maxVisibleTags &&
        this.tagContainerWidth > 0
      );
    },
    isLoading() {
      return !this.loaded;
    },
  },
  watch: {
    value(newValue) {
      this.selectedValuesInternal = newValue || [];
    },
    url(newUrl) {
      this.handleUrlChange(newUrl);
    },
    loaded(load) {
      this.handleLoading(load);
    },
    isTyping(typing) {
      const _th = this
      this.$nextTick(() => {
        const selectInput = document.querySelector(`[name=${_th.name}] .el-select__tags`)

        if(!selectInput) { return }
        
        if(typing) {
          const spinner = document.createElement('i');
          spinner.className = 'position-relative loading-spinner fas fa-spinner fa-pulse';

          selectInput.appendChild(spinner);
        }else {
          const spinnerToRemove = document.querySelector(`[name=${_th.name}] .el-select__tags .loading-spinner`)
          spinnerToRemove.remove()
        }
      })
    }
  },
  methods: {
    handleLoading() {
      this.onChangeDropDownPopper(true)
    },
    handleUrlChange(newUrl) {
      if (this.currentUrl !== newUrl) {
        this.loaded = false;
        this.currentUrl = newUrl;
        this.fetchOptions();
      }
    },
    handleValueChange(newValue) {
      this.selectedValuesInternal = newValue || [];
    },
    fetchOptions() {
      if (!this.loaded) {
        this.axios
          .get(this.currentUrl)
          .then((response) => {
            this.options = response.data;
            this.tempOptions = response.data;
            this.$emit('options-loaded', this.options);
          })
          .catch((error) => {
            console.error('Erro ao carregar opções:', error);
          })
          .finally(() => {
            this.loaded = true;
          });
      }
    },
    handleSelectChange(selectedValues) {
      this.selectedValuesInternal = selectedValues;
      this.updateTagDisplay()
      this.$emit('input', this.selectedValuesInternal);
    },
    updateTagDisplay() {
      // largura das tags após a renderização
      this.tagContainerWidth = this.$refs[`select-${this.selectId}`].$el.querySelector('.el-input__inner').offsetWidth;
    },
    debounce(fn, delay) {
      let timeoutId;
      const _th = this
      return function(...args) {
        _th.isTyping = true;
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
          fn.apply(this, args);

          _th.isTyping = false;
        }, delay);
      };
    },
    filterGroup(query) {
      const queryLabel = query.toLowerCase();

      const filterList = (listOptions) => {
        return listOptions.map(option => {
          const parentLabel = option.label.toLowerCase();
          const matchesParent = parentLabel.indexOf(queryLabel) > -1;

          if (matchesParent) {
            return { ...option };
          }

          let filteredChildren = [];
          if (option.children) {
            filteredChildren = filterList(option.children);
          }

          if (filteredChildren.length > 0) {
            return {
              ...option,
              children: filteredChildren
            };
          }

          return null;
        }).filter(option => option !== null); // Remove null
      };

      this.options = filterList(this.tempOptions);
      this.onChangeDropDownPopper(true)
    },
    onChangeDropDownPopper(isOpen) {
      if (!isOpen) {
        this.options = [...this.tempOptions];
      } else {
        this.$nextTick(() => {
          const dropDown = document.querySelector(`.popper-${this.selectId}`);
          
          if (dropDown) {
            const titles = dropDown.querySelectorAll('.el-select-group__title');
      
            if (titles.length === 0) { return; }
      
            titles.forEach((title) => {
              title.removeEventListener('click', this.handleGroupClick);
              title.addEventListener('click', this.handleGroupClick);
            });
          }
        });
      }
    },
    handleGroupClick(event) {
      const groupLabel = event.target.textContent.trim();
      const group = this.tempOptions.find(opt => opt.label === groupLabel);

      if (group && group.children) {
        const childrenIds = group.children.map(child => child.id);

        const allChildrenSelected = childrenIds.every(id => this.selectedValuesInternal.includes(id));

        if (allChildrenSelected) {
          this.selectedValuesInternal = this.selectedValuesInternal.filter(id => !childrenIds.includes(id));
        } else {
          this.selectedValuesInternal = [...new Set([...this.selectedValuesInternal, ...childrenIds])];
        }

        this.$emit('input', this.selectedValuesInternal);
      }
    }
  },
  mounted() {
    this.debouncedFilterGroup = this.debounce(this.filterGroup, 500);

    window.addEventListener('resize', this.updateTagDisplay);
    this.$nextTick(this.updateTagDisplay); 
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateTagDisplay);
  },
  created() {
    this.selectId = this.generateSecureId();
    this.name = `customMultiselect-${this.generateSecureId()}`
  },
};
</script>
<style lang="scss">
  .el-select-group__title {
    cursor: pointer;
    transition: 0.2s;
    &:hover {
      background: #e8edf4;
      color: #70a8db;
      font-weight: 600;
    }  
  }
</style>
